import { FormControl, FormHelperText, FormLabel, PropTypes } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import PhotoUpload from 'components/common/photoUpload'
import { Image, ImageOptions, useImages } from 'components/forms/helpers/useImages'
import { useSnackbar } from 'notistack'
import React from 'react'
import { Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'

interface UploaderProps {
    classes?: {
        root?: string
        container?: string
    }
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        label: {
            flexGrow: 1,
            fontWeight: theme.fontsWeight.semiBold,
            marginBottom: theme.spacing(2),
        },
    }),
)

interface Props {
    name: string
    label?: string | React.ReactNode
    defaultValue?: Image
    children: (
        Uploader: React.FC<UploaderProps>,
        hasError: boolean,
        error?: string,
        image?: Image,
        onDelete?: () => void,
    ) => React.ReactNode
    margin?: PropTypes.Margin
    required?: boolean
    imageOptions?: ImageOptions
}

const ImageField: React.FC<Props> = ({
    name,
    defaultValue,
    children,
    margin = 'normal',
    required,
    imageOptions,
    label,
}) => {
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()
    const { updateImages } = useImages(imageOptions)
    const classes = useStyles()

    return (
        <Field name={name} defaultValue={defaultValue}>
            {({ input: { onChange, value }, meta }) => {
                const hasError =
                    ((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched
                return (
                    <FormControl
                        component="fieldset"
                        fullWidth
                        margin={margin}
                        required={required}
                        error={hasError}
                    >
                        {label && (
                            <FormLabel component="legend" className={classes.label}>
                                {label}
                            </FormLabel>
                        )}
                        {children(
                            ({ classes, children }) => (
                                <PhotoUpload
                                    classes={classes}
                                    multiple={true}
                                    files={[]}
                                    accept={['image/jpeg', 'image/png']}
                                    onChange={(files) => {
                                        if (files !== undefined) {
                                            updateImages(files, []).then((images) => {
                                                onChange(images[0])
                                            })
                                        }
                                    }}
                                    // 5000000 = 5 MB
                                    maxSize={parseInt(
                                        process.env.REACT_APP_PHOTO_MAX_SIZE !== undefined
                                            ? process.env.REACT_APP_PHOTO_MAX_SIZE
                                            : '5000000',
                                    )}
                                    isInvalidFiles={(files) => {
                                        for (let i = 0; i < files.length; i++) {
                                            enqueueSnackbar(
                                                t('form-validation.photo').replace(
                                                    ':name',
                                                    files[i].file.name,
                                                ),
                                                {
                                                    variant: 'error',
                                                },
                                            )
                                        }
                                        return true
                                    }}
                                >
                                    {children}
                                </PhotoUpload>
                            ),
                            hasError || false,
                            meta.error,
                            value,
                            () => {
                                onChange(undefined)
                            },
                        )}
                        {hasError && <FormHelperText error={hasError}>{meta.error}</FormHelperText>}
                    </FormControl>
                )
            }}
        </Field>
    )
}

export default ImageField
